import { DepositMethodType, WithdrawWayType, DepositActionType, CurrencyType, ActivityType, NowTime } from '@golden/gdk-base';
import { endOfDay, subMonths, subWeeks, subDays, getUnixTime, startOfDay, startOfWeek, endOfWeek, startOfMonth, startOfYesterday, endOfYesterday, endOfMonth, startOfYear, addDays, addHours, isLastDayOfMonth, getTime, isAfter, addMonths, isBefore, isEqual, addWeeks, subYears, addYears } from 'date-fns';
import { utcToZonedTime, formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import { BcMath } from '@golden/bcmath';
import html2canvas from 'html2canvas';
import { Subject } from 'rxjs';

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);

// src/utils/BankImage.ts
var getBankImage = (bank) => {
  return new URL(`./assets/banks/${bank.toUpperCase()}.png`, import.meta.url).href;
};
var getBankIcon = (bank) => {
  return new URL(`./assets/bankIcons/${bank.toUpperCase()}.png`, import.meta.url).href;
};
var ONE_HOUR_IN_MS = 60 * 60 * 1e3;
var ONE_DAY_IN_HOURS = 24;
var THREE_HOURS_IN_MS = 3 * ONE_HOUR_IN_MS;
var ONE_DAY_IN_MS = ONE_DAY_IN_HOURS * ONE_HOUR_IN_MS;
var THREE_DAYS_IN_MS = 3 * ONE_DAY_IN_HOURS * ONE_HOUR_IN_MS;
var PERMANENT_TIME = 25340227194e4;
function getSubFnBySubType(timeType) {
  if (timeType === "month") return subMonths;
  if (timeType === "year") return subYears;
  if (timeType === "day") return subDays;
  throw new Error("timeType must be month, year or day. Got " + String(timeType));
}
function getAddFnBySubType(timeType) {
  if (timeType === "month") return addMonths;
  if (timeType === "year") return addYears;
  if (timeType === "day") return addDays;
  throw new Error("timeType must be month, year or day. Got " + String(timeType));
}
var TAIPEI_TIMEZONE = "+08";
function getTaipeiTime(utc) {
  return utcToZonedTime(utc, TAIPEI_TIMEZONE);
}
function getTaipeiNowTime() {
  return getTaipeiTime(NowTime.get());
}
function formatTaipeiTime(date, pattern) {
  return formatInTimeZone(date, TAIPEI_TIMEZONE, pattern);
}
function createTaipeiDateFunc(func) {
  return (...args) => zonedTimeToUtc(func(...args), TAIPEI_TIMEZONE);
}
var startOfDayTaipei = createTaipeiDateFunc(startOfDay);
var endOfDayTaipei = createTaipeiDateFunc(endOfDay);
var startOfYesterdayTaipei = createTaipeiDateFunc(startOfYesterday);
var endOfYesterdayTaipei = createTaipeiDateFunc(endOfYesterday);
var startOfWeekTaipei = createTaipeiDateFunc(startOfWeek);
var endOfWeekTaipei = createTaipeiDateFunc(endOfWeek);
var startOfMonthTaipei = createTaipeiDateFunc(startOfMonth);
var endOfMonthTaipei = createTaipeiDateFunc(endOfMonth);
var startOfYearTaipei = createTaipeiDateFunc(startOfYear);
function setToday() {
  return { start: startOfDay(/* @__PURE__ */ new Date()), end: endOfDay(/* @__PURE__ */ new Date()) };
}
function setYesterday() {
  const yesterday = subDays(/* @__PURE__ */ new Date(), 1);
  return { start: startOfDay(yesterday), end: endOfDay(yesterday) };
}
function setTomorrow() {
  const tomorrow = addDays(/* @__PURE__ */ new Date(), 1);
  return { start: startOfDay(tomorrow), end: endOfDay(tomorrow) };
}
function setLastSevenDays() {
  return { start: startOfDay(subDays(/* @__PURE__ */ new Date(), 6)), end: endOfDay(/* @__PURE__ */ new Date()) };
}
function setThisWeek() {
  return { start: startOfWeek(/* @__PURE__ */ new Date(), { weekStartsOn: 1 }), end: endOfDay(/* @__PURE__ */ new Date()) };
}
function setLastWeek() {
  const oneDayInLastWeek = subWeeks(/* @__PURE__ */ new Date(), 1);
  return { start: startOfWeek(oneDayInLastWeek, { weekStartsOn: 1 }), end: endOfWeek(oneDayInLastWeek, { weekStartsOn: 1 }) };
}
function setThisMonth() {
  return { start: startOfMonth(/* @__PURE__ */ new Date()), end: endOfDay(/* @__PURE__ */ new Date()) };
}
function setThisMonthToYesterday() {
  return { start: startOfMonth(/* @__PURE__ */ new Date()), end: endOfDay(subDays(/* @__PURE__ */ new Date(), 1)) };
}
function setLastMonth() {
  const oneDayInLastMonth = subMonths(/* @__PURE__ */ new Date(), 1);
  return { start: startOfMonth(oneDayInLastMonth), end: endOfMonth(oneDayInLastMonth) };
}
function setLastTwoMonths() {
  const start = startOfDay(subMonths(/* @__PURE__ */ new Date(), 2));
  const end = endOfDay(/* @__PURE__ */ new Date());
  return { start, end };
}
function setUpcomingDays(diff) {
  const start = startOfDay(/* @__PURE__ */ new Date());
  const end = endOfDay(addDays(/* @__PURE__ */ new Date(), diff));
  return { start, end };
}
function setUpcomingDaysFromNow(diff, start = Date.now()) {
  const end = addDays(start, diff);
  return { start, end };
}
function setUpcomingHoursFromNow(diff, start = Date.now()) {
  const end = addHours(start, diff);
  return { start, end };
}
function setPermanent() {
  const start = /* @__PURE__ */ new Date();
  const end = endOfDay(new Date(PERMANENT_TIME));
  return { start, end };
}
function getAllowedTimeStamps(startTimeStamp, endTimeStamp, range = 3, timeType = "month") {
  const subFn = getSubFnBySubType(timeType);
  const addFn = getAddFnBySubType(timeType);
  let maxAllowedEndTimeStamp = 0;
  if (isLastDayOfMonth(startTimeStamp)) {
    maxAllowedEndTimeStamp = getTime(endOfDay(endOfMonth(addFn(new Date(startTimeStamp), range))));
  } else {
    maxAllowedEndTimeStamp = getTime(endOfDay(addFn(new Date(startTimeStamp), range)));
  }
  return {
    minAllowedStartTimeStamp: getTime(startOfDay(subFn(new Date(endTimeStamp), range))),
    maxAllowedEndTimeStamp
  };
}
function createShouldDisableFuture() {
  const shouldDisableStartDate = (day) => {
    if (day === null) return true;
    return isAfter(day, endOfDay(/* @__PURE__ */ new Date()));
  };
  const shouldDisableEndDate = (day) => {
    if (day === null) return true;
    return isAfter(day, endOfDay(/* @__PURE__ */ new Date()));
  };
  return {
    shouldDisableStartDate,
    shouldDisableEndDate
  };
}
function getCorrectedDateRange(value, maxMonthsRange = 3) {
  if (!value.start || !value.end) return value;
  if (isAfter(value.end, addMonths(value.start, maxMonthsRange))) {
    if (value.key === "start") {
      return { start: value.start, end: endOfDay(addMonths(value.start, maxMonthsRange)) };
    } else {
      return { start: startOfDay(subMonths(value.end, maxMonthsRange)), end: value.end };
    }
  }
  return value;
}
function createShouldDisableDateBetween(startTime, endTime, range = 3, timeType = "month") {
  const subFn = getSubFnBySubType(timeType);
  const addFn = getAddFnBySubType(timeType);
  const today = endOfDay(/* @__PURE__ */ new Date());
  const shouldDisableStartDate = (day) => {
    if (day === null) return true;
    if (isAfter(day, today)) return true;
    if (endTime === null) return false;
    if (isAfter(day, endOfDay(endTime))) return true;
    const earliestAllowedDate = startOfDay(subFn(endTime, range));
    return isBefore(day, earliestAllowedDate);
  };
  const shouldDisableEndDate = (day) => {
    if (day === null) return true;
    if (isAfter(day, today)) return true;
    if (startTime === null) return false;
    if (isBefore(day, startOfDay(startTime))) return true;
    const latestAllowedDate = endOfDay(addFn(startTime, range));
    if (isAfter(day, latestAllowedDate)) return true;
    return false;
  };
  return {
    shouldDisableStartDate,
    shouldDisableEndDate
  };
}
function createShouldDisableDate(startTime, endTime, mustAfter, mustBefore) {
  const shouldDisableStartDate = (day) => {
    if (day === null) return true;
    if (isBefore(day, mustAfter)) return true;
    if (mustBefore && isAfter(day, endOfDay(mustBefore))) return true;
    if (endTime === null) return false;
    return isAfter(day, endOfDay(endTime));
  };
  const shouldDisableEndDate = (day) => {
    if (day === null) return true;
    if (mustBefore && isAfter(day, endOfDay(mustBefore))) return true;
    if (startTime === null) return isBefore(day, mustAfter);
    return isBefore(day, startOfDay(startTime));
  };
  return {
    shouldDisableStartDate,
    shouldDisableEndDate
  };
}
function toTimezonedDate(date) {
  return utcToZonedTime(date, "+0800");
}
function getMonthArray(start, end) {
  const monthStart = startOfMonth(start);
  const monthEnd = startOfMonth(end);
  let months = [];
  for (let monthCursor = monthStart; isBefore(monthCursor, monthEnd) || isEqual(monthCursor, monthEnd); monthCursor = addMonths(monthCursor, 1)) {
    months = months.concat(monthCursor);
  }
  return months;
}
function getWeekArray(start, end) {
  const weekStart = startOfWeek(start, { weekStartsOn: 1 });
  const weekEnd = startOfWeek(end, { weekStartsOn: 1 });
  let weeks = [];
  for (let weekCursor = weekStart; isBefore(weekCursor, weekEnd) || isEqual(weekCursor, weekEnd); weekCursor = addWeeks(weekCursor, 1)) {
    weeks = weeks.concat(weekCursor);
  }
  return weeks;
}
function getDayArray(start, end) {
  const dayStart = startOfDay(start);
  const dayEnd = startOfDay(end);
  let days = [];
  for (let dayCursor = dayStart; isBefore(dayCursor, dayEnd) || isEqual(dayCursor, dayEnd); dayCursor = addDays(dayCursor, 1)) {
    days = days.concat(dayCursor);
  }
  return days;
}

// src/utils/DateQuery.ts
var getTaipeiDefaultDateRangeOfToday = () => [startOfDayTaipei(getTaipeiNowTime()), endOfDayTaipei(getTaipeiNowTime())];
var getDefaultDateRangeOfToday = () => [startOfDay(NowTime.get()), endOfDay(NowTime.get())];
var getTaipeiRangeOfYesterday = () => {
  const taipeiYesterday = subDays(getTaipeiNowTime(), 1);
  return [startOfDayTaipei(taipeiYesterday), endOfDayTaipei(taipeiYesterday)];
};
var getRangeOfYesterday = () => [startOfDay(subDays(/* @__PURE__ */ new Date(), 1)), endOfDay(subDays(/* @__PURE__ */ new Date(), 1))];
var getAllowDateRangeOfOneMonths = () => ({ from: subMonths(endOfDay(NowTime.get()), 1), to: endOfDay(NowTime.get()) });
var getTaipeiAllowDateRangeOfTwoWeeks = () => ({ from: subWeeks(endOfDayTaipei(getTaipeiNowTime()), 2), to: endOfDayTaipei(getTaipeiNowTime()) });
var getAllowDateRangeOfTwoWeeks = () => ({ from: subWeeks(endOfDay(NowTime.get()), 2), to: endOfDay(NowTime.get()) });
var getAllowOffsetDaysRange = (offsetDays) => ({ from: subDays(endOfDay(NowTime.get()), offsetDays), to: endOfDay(NowTime.get()) });
var getTaipeiAllowOffsetDaysRange = (offsetDays) => ({ from: subDays(endOfDayTaipei(getTaipeiNowTime()), offsetDays), to: endOfDayTaipei(getTaipeiNowTime()) });
var getDateQuery = (dateRange) => ({ start: getUnixTime(dateRange[0]), end: getUnixTime(isBefore(endOfDay(dateRange[1]), NowTime.get()) ? endOfDay(dateRange[1]) : NowTime.get()) });
var getDateAtQuery = (dateRange) => ({ start_at: getUnixTime(dateRange[0]), end_at: getUnixTime(isBefore(endOfDay(dateRange[1]), NowTime.get()) ? endOfDay(dateRange[1]) : NowTime.get()) });
var getThisWeek = () => [startOfWeek(NowTime.get(), { weekStartsOn: 1 }), endOfDay(NowTime.get())];
var getTaipeiThisWeek = () => [startOfWeekTaipei(getTaipeiNowTime(), { weekStartsOn: 1 }), endOfDayTaipei(getTaipeiNowTime())];
var getLastWeek = () => [startOfWeek(subWeeks(NowTime.get(), 1), { weekStartsOn: 1 }), endOfWeek(subWeeks(NowTime.get(), 1), { weekStartsOn: 1 })];
var getTaipeiLastWeek = () => [startOfWeekTaipei(subWeeks(getTaipeiNowTime(), 1), { weekStartsOn: 1 }), endOfWeekTaipei(subWeeks(getTaipeiNowTime(), 1), { weekStartsOn: 1 })];
var getThisMonth = () => [startOfMonth(NowTime.get()), endOfDay(NowTime.get())];
var isEditableSystemActivitiesHelper = (type) => type !== "" && [ActivityType.WEEKLY_BONUS_GOLD].includes(type);
var _IdleStorage = class _IdleStorage {
  constructor(key) {
    __publicField(this, "idleStorage");
    __publicField(this, "key");
    this.key = key;
    this.idleStorage = this.createStorage();
  }
  static getInstance(key) {
    if (!_IdleStorage.instance) {
      _IdleStorage.instance = new _IdleStorage(key);
    }
    return _IdleStorage.instance;
  }
  static reset() {
    _IdleStorage.instance = null;
  }
  createStorage() {
    let fallbackLastActiveTime = null;
    return {
      get lastActiveTime() {
        var _a;
        if (!_IdleStorage.instance) return null;
        try {
          return Number((_a = window.localStorage.getItem(_IdleStorage.instance.key)) != null ? _a : getUnixTime(/* @__PURE__ */ new Date()));
        } catch (e) {
          return fallbackLastActiveTime != null ? fallbackLastActiveTime : getUnixTime(/* @__PURE__ */ new Date());
        }
      },
      set lastActiveTime(value) {
        if (!_IdleStorage.instance) return;
        try {
          window.localStorage.setItem(_IdleStorage.instance.key, value.toString());
        } catch (e) {
          fallbackLastActiveTime = value;
        }
      },
      clear() {
        var _a;
        if (!_IdleStorage.instance) return;
        try {
          (_a = window.localStorage) == null ? void 0 : _a.removeItem(_IdleStorage.instance.key);
        } catch (e) {
          fallbackLastActiveTime = null;
        }
      }
    };
  }
  updateActiveTime() {
    this.idleStorage.lastActiveTime = getUnixTime(/* @__PURE__ */ new Date());
  }
  isIdle(idle) {
    if (!this.idleStorage.lastActiveTime) return false;
    const now = getUnixTime(/* @__PURE__ */ new Date());
    return now - this.idleStorage.lastActiveTime > idle;
  }
  clear() {
    this.idleStorage.clear();
  }
  getKey() {
    return this.key;
  }
};
__publicField(_IdleStorage, "instance", null);
var IdleStorage = _IdleStorage;
var getIdleStorage = (key) => {
  return IdleStorage.getInstance(key);
};
var registerIdleLogout = (payload) => {
  const { isLoginSubject, logout, idle = 2 * 60 * 60, key = "IDLE:LAST_ACTIVE_TIME_KEY_V4" } = payload;
  const storage = IdleStorage.getInstance(key);
  const checkIdleLogout = () => {
    if (storage.isIdle(idle)) {
      logout();
      storage.clear();
      return;
    }
    storage.updateActiveTime();
  };
  const subscription = isLoginSubject.subscribe((isLogin) => {
    if (isLogin) {
      checkIdleLogout();
      window.addEventListener("click", checkIdleLogout, { passive: true });
    } else {
      window.removeEventListener("click", checkIdleLogout);
      storage.clear();
    }
  });
  return () => {
    if (!subscription.closed) {
      subscription.unsubscribe();
    }
    window.removeEventListener("click", checkIdleLogout);
  };
};

// src/utils/Image.ts
var TRANSPARENT_IMAGE = `data:image/png;base64,iVBORw0KGgoAAAANSUhEUg
AAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=`;

// src/utils/PlayerRankImage.ts
var getPlayerRankImage = (rank) => {
  return new URL(`./assets/rank/icons/${rank}.png`, import.meta.url).href;
};

// src/utils/announcementIcon.ts
var announcementIcon = (type, isRead = false) => {
  return new URL(`./assets/message/${type}/${isRead ? "read" : "unread"}.svg`, import.meta.url).href;
};

// src/utils/copyString.ts
var copyString = (content) => {
  const node = document.createElement("textarea");
  node.value = content;
  document.body.append(node);
  node.select();
  document.execCommand("copy");
  node.remove();
};
function goldenCashFormat(value) {
  if (value === "") return "";
  return BcMath.base(value).goldenFormat();
}
var htmlToCanvas = html2canvas;
var canvasToImage = (canvas) => canvas.toDataURL();

// src/utils/loadScript.ts
var loadScript = async (url, preconnect = true) => {
  return await new Promise((resolve, reject) => {
    const head = document.head || document.getElementsByTagName("head")[0];
    const script = document.createElement("script");
    script.async = true;
    script.src = url;
    if (preconnect) {
      const link = document.createElement("link");
      const { origin } = new URL(url);
      link.href = origin;
      link.rel = "preconnect";
      head.appendChild(link);
    }
    head.appendChild(script);
    script.onload = resolve;
    script.onerror = reject;
  });
};
function normalizeMaxNumber(data, max, removeZero = true) {
  let result = data.replace(/[^0-9]/g, "");
  if (removeZero) result = result.replace(/^0+/, "");
  if (result !== "" && Number(BcMath.base(result).comp(max)) === 1) {
    result = max;
  }
  return result;
}
function normalizeMinNumber(data, min, removeZero = true) {
  let result = data.replace(/[^0-9]/g, "");
  if (removeZero) result = result.replace(/^0+/, "");
  if (result.length === 0 || Number(BcMath.base(result).comp(min)) === -1) {
    result = min;
  }
  return result;
}
function getMaxInteger(cash) {
  return cash.split(".").length > 0 ? cash.split(".")[0] : cash;
}
function filterHTMLTags(html) {
  return html.replace(/\n/g, " ").replace(/<[^>]*>/g, "").replace(/&gt;/g, ">").replace(/&lt;/g, "<");
}
function recursivelyParseJSON(obj) {
  for (const key in obj) {
    const value = obj[key];
    if (typeof value === "string") {
      try {
        const nested = JSON.parse(value);
        if (typeof nested === "object" && nested !== null) {
          obj[key] = recursivelyParseJSON(nested);
        }
      } catch (e) {
      }
    } else if (typeof value === "object" && value !== null) {
      obj[key] = recursivelyParseJSON(value);
    }
  }
  return obj;
}
function normalizeAlphanumeric(data) {
  return data.replace(/[^a-zA-Z0-9]/g, "");
}
function normalizeNestedJson(jsonString, whiteSpace = 2) {
  if (!jsonString) return "";
  try {
    const parsedObject = JSON.parse(jsonString);
    const processedObject = recursivelyParseJSON(parsedObject);
    return JSON.stringify(processedObject, null, whiteSpace);
  } catch (error) {
    console.error("Failed to parse initial JSON string:", error instanceof Error ? error.message : error);
    return "";
  }
}

// src/utils/openQQ.ts
var openQQ = (id) => {
  const ua = navigator.userAgent;
  const ipad = !!ua.match(/(iPad).*OS\s([\d_]+)/);
  const isIphone = !ipad && !!ua.match(/(iPhone\sOS)\s([\d_]+)/);
  const isAndroid = !!ua.match(/(Android)\s+([\d.]+)/);
  if (isIphone) {
    location.href = `mqqapi://card/show_pslcard?src_type=internal&version=1&uin=${id}&card_type=person&source=sharecard`;
  } else if (isAndroid) {
    location.href = `mqqapi://card/show_pslcard?src_type=internal&version=1&uin=${id}&card_type=person&source=sharecard`;
  } else {
    location.href = `tencent://AddContact/?fromId=45&fromSubId=1&subcmd=all&uin=${id}`;
  }
};

// src/utils/openWindow.ts
var openWindow = (name, href, width = 500, height = 500, windowFeature = `
location=yes,
resizable=yes,
scrollbars=yes,
status=yes,
width=${width},
height=${height}
`) => {
  const win = window.open(href, name, windowFeature);
  if (!win) throw new Error("Can't open new window.");
  return win;
};
var LOADING_HTML = '<center><p style="font-size: 24px;">Loading...</p></center>';
var setWindowLoading = (win, html = LOADING_HTML) => {
  var _a;
  try {
    const meta1 = win.document.createElement("meta");
    meta1.setAttribute("name", "viewport");
    meta1.setAttribute("content", "width=device-width,initial-scale=1,maximum-scale=1,minimum-scale=1,user-scalable=no,viewport-fit=cover");
    const head = (_a = win.document.getElementsByTagName("head")) == null ? void 0 : _a[0];
    head.appendChild(meta1);
    win.document.body.innerHTML = html;
  } catch (e) {
    console.error(e);
  }
};

// src/utils/scrollIntoView.ts
function scrollIntoView(dom) {
  if (!dom) return;
  const [x, y] = [window.scrollX, window.scrollY];
  dom.scrollIntoView();
  window.scrollTo(x, y);
}

// src/utils/scrollMore.ts
var scrollMore = (loadMore, tolerance = 10) => (e) => {
  const target = e.target;
  if (target.scrollTop + target.clientHeight < target.scrollHeight - tolerance) return;
  loadMore();
};

// src/utils/setViewportOnDesktop.ts
function setViewportOnDesktop() {
  if (window.document.body.clientWidth >= 1280) return;
  const viewportMeta = document.querySelector("meta[name=viewport]");
  viewportMeta == null ? void 0 : viewportMeta.setAttribute("content", "width=1280");
}

// src/utils/supportWebp.ts
var supportWebp = () => {
  var _a;
  const canvas = document.createElement("canvas");
  return ((_a = canvas.getContext) == null ? void 0 : _a.call(canvas, "2d")) && !canvas.toDataURL("image/webp").indexOf("data:image/webp");
};
var getWebpClass = () => supportWebp() ? "webp" : "no-webp";
function createTokenRefreshHandler(refreshToken) {
  let refreshSubject$ = null;
  return (error) => {
    if (!refreshSubject$) {
      refreshSubject$ = new Subject();
      refreshToken().subscribe({
        next: () => {
          refreshSubject$ == null ? void 0 : refreshSubject$.next();
          refreshSubject$ == null ? void 0 : refreshSubject$.complete();
          refreshSubject$ = null;
        },
        error: () => {
          refreshSubject$ == null ? void 0 : refreshSubject$.error(error);
          refreshSubject$ = null;
        }
      });
    }
    return refreshSubject$.asObservable();
  };
}

// src/cashflow/depositMethods/interface/DepositMethodAction.ts
var ActionKind = /* @__PURE__ */ ((ActionKind2) => {
  ActionKind2[ActionKind2["COMPONENT"] = 0] = "COMPONENT";
  ActionKind2[ActionKind2["JS"] = 1] = "JS";
  return ActionKind2;
})(ActionKind || {});

// src/cashflow/depositMethods/DepositMethodBase.ts
var DepositMethodBase = class {
  constructor(openWindowAction, vt = (key) => key) {
    this.openWindowAction = openWindowAction;
    this.vt = vt;
    __publicField(this, "_commonAction", {
      [DepositActionType.ACCESS]: {
        kind: 1 /* JS */,
        name: "OpenWindow",
        script: (data, argument) => this.openWindowAction(data, argument),
        argument: {
          width: 500,
          height: 500,
          title: () => this.vt(`depositMethod.${this.id}.name`)
        }
      },
      [DepositActionType.QRCODE]: {
        kind: 0 /* COMPONENT */,
        name: "QRCodeModal",
        argument: {
          account: ""
        }
      },
      [DepositActionType.QRCODE_GOUBAO]: {
        kind: 0 /* COMPONENT */,
        name: "GoubaoQRCodeModal",
        argument: null
      },
      [DepositActionType.SHOW_BANK]: {
        kind: 0 /* COMPONENT */,
        name: "ShowInfoModal",
        argument: null
      },
      [DepositActionType.SHOW_CRYPTO]: {
        kind: 0 /* COMPONENT */,
        name: "CryptoInfoModal",
        argument: null
      },
      [DepositActionType.PENDING]: {
        kind: 0 /* COMPONENT */,
        name: "Pending",
        argument: null
      }
    });
  }
  getComponents() {
    return this._components;
  }
  getActionWrapper(forwarderId, actionId) {
    if (forwarderId in this._specialActionComponents && actionId in this._specialActionComponents[forwarderId]) {
      return this._specialActionComponents[forwarderId][actionId];
    }
    return this._commonAction[actionId];
  }
};
var DepositCGPay = class extends DepositMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", DepositMethodType.CGPAY);
    __publicField(this, "bindCardRequired", false);
    __publicField(this, "hasDownloadLink", true);
    __publicField(this, "openAgentLayer", true);
    __publicField(this, "_components", {
      middle: {
        name: "DepositCGPayApplyBlock"
      },
      bottom: {
        name: "DepositCGPayNoteBlock"
      }
    });
    __publicField(this, "_specialActionComponents", {});
  }
};
var DepositCryptoErc = class extends DepositMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", DepositMethodType.CRYPTO_ERC);
    __publicField(this, "bindCardRequired", false);
    __publicField(this, "hasDownloadLink", false);
    __publicField(this, "openAgentLayer", true);
    __publicField(this, "_components", {
      middle: {
        name: "DepositCryptoApplyBlock"
      },
      bottom: {
        name: "DepositCryptoNoteBlock"
      }
    });
    __publicField(this, "_specialActionComponents", {});
  }
};
var DepositCryptoTrc = class extends DepositMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", DepositMethodType.CRYPTO_TRC);
    __publicField(this, "bindCardRequired", false);
    __publicField(this, "hasDownloadLink", false);
    __publicField(this, "openAgentLayer", true);
    __publicField(this, "_components", {
      middle: {
        name: "DepositCryptoApplyBlock"
      },
      bottom: {
        name: "DepositCryptoNoteBlock"
      }
    });
    __publicField(this, "_specialActionComponents", {});
  }
};
var DepositScanWechat = class extends DepositMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", DepositMethodType.SCAN_WECHAT);
    __publicField(this, "bindCardRequired", false);
    __publicField(this, "hasDownloadLink", false);
    __publicField(this, "openAgentLayer", false);
    __publicField(this, "_components", {
      middle: {
        name: "DepositApplyBlock"
      },
      bottom: {
        name: "DepositOnlineScanNoteBlock"
      }
    });
    __publicField(this, "_specialActionComponents", {});
    __publicField(this, "_commonAction", {
      ...this._commonAction,
      [DepositActionType.QRCODE]: {
        kind: 0 /* COMPONENT */,
        name: "QRCodeModal",
        argument: {
          account: this.vt("depositMethod.scan_wechat.qrcode.account")
        }
      }
    });
  }
};
var DepositScanAlipay = class extends DepositMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", DepositMethodType.SCAN_ALIPAY);
    __publicField(this, "bindCardRequired", false);
    __publicField(this, "hasDownloadLink", false);
    __publicField(this, "openAgentLayer", true);
    __publicField(this, "_components", {
      middle: {
        name: "DepositApplyBlock"
      },
      bottom: {
        name: "DepositOnlineScanNoteBlock"
      }
    });
    __publicField(this, "_specialActionComponents", {});
    __publicField(this, "_commonAction", {
      ...this._commonAction,
      [DepositActionType.QRCODE]: {
        kind: 0 /* COMPONENT */,
        name: "QRCodeModal",
        argument: {
          account: this.vt("depositMethod.scan_alipay.qrcode.account")
        }
      }
    });
  }
};
var DepositOnline = class extends DepositMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", DepositMethodType.ONLINE);
    __publicField(this, "bindCardRequired", true);
    __publicField(this, "hasDownloadLink", false);
    __publicField(this, "openAgentLayer", true);
    __publicField(this, "_components", {
      middle: {
        name: "DepositApplyBlock"
      },
      bottom: {
        name: "DepositOnlineScanNoteBlock"
      }
    });
    __publicField(this, "_specialActionComponents", {});
  }
};
var DepositOnlineManual = class extends DepositMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", DepositMethodType.ONLINE_MANUAL);
    __publicField(this, "bindCardRequired", false);
    __publicField(this, "hasDownloadLink", false);
    __publicField(this, "openAgentLayer", false);
    __publicField(this, "_components", {
      middle: {
        name: "DepositApplyBlock"
      },
      bottom: {
        name: "DepositOnlineScanNoteBlock"
      }
    });
    __publicField(this, "_specialActionComponents", {});
  }
};
var DepositGoubao = class extends DepositMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", DepositMethodType.GOUBAO);
    __publicField(this, "bindCardRequired", false);
    __publicField(this, "hasDownloadLink", true);
    __publicField(this, "openAgentLayer", false);
    __publicField(this, "_components", {
      middle: {
        name: "DepositGoubaoApplyBlock"
      },
      bottom: {
        name: "DepositGoubaoNoteBlock"
      }
    });
    __publicField(this, "_specialActionComponents", {});
    __publicField(this, "_commonAction", {
      ...this._commonAction,
      [DepositActionType.QRCODE_GOUBAO]: {
        kind: 0 /* COMPONENT */,
        name: "GoubaoQRCodeModal",
        argument: null
      }
    });
  }
};
var DepositTopay = class extends DepositMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", DepositMethodType.TOPAY);
    __publicField(this, "bindCardRequired", false);
    __publicField(this, "hasDownloadLink", true);
    __publicField(this, "openAgentLayer", true);
    __publicField(this, "_components", {
      middle: {
        name: "DepositApplyBlock"
      },
      bottom: {
        name: "DepositTopayNoteBlock"
      }
    });
    __publicField(this, "_specialActionComponents", {});
  }
};
var DepositEbpay = class extends DepositMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", DepositMethodType.EBPAY);
    __publicField(this, "bindCardRequired", false);
    __publicField(this, "hasDownloadLink", true);
    __publicField(this, "openAgentLayer", true);
    __publicField(this, "_components", {
      middle: {
        name: "DepositApplyBlock"
      },
      bottom: {
        name: "DepositEbpayNoteBlock"
      }
    });
    __publicField(this, "_specialActionComponents", {});
  }
};
var DepositUnionPayQuick = class extends DepositMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", DepositMethodType.UNION_PAY_QUICK);
    __publicField(this, "bindCardRequired", true);
    __publicField(this, "hasDownloadLink", false);
    __publicField(this, "openAgentLayer", true);
    __publicField(this, "_components", {
      middle: {
        name: "DepositApplyBlock"
      },
      bottom: {
        name: "DepositUnionPayCommonNoteBlock"
      }
    });
    __publicField(this, "_specialActionComponents", {});
  }
};
var DepositKOIPay = class extends DepositMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", DepositMethodType.KOIPAY);
    __publicField(this, "bindCardRequired", false);
    __publicField(this, "hasDownloadLink", true);
    __publicField(this, "openAgentLayer", true);
    __publicField(this, "_components", {
      middle: {
        name: "DepositApplyBlock"
      },
      bottom: {
        name: "DepositKoipayNoteBlock"
      }
    });
    __publicField(this, "_specialActionComponents", {});
  }
};
var DepositECNY = class extends DepositMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", DepositMethodType.E_CNY);
    __publicField(this, "bindCardRequired", false);
    __publicField(this, "hasDownloadLink", true);
    __publicField(this, "openAgentLayer", true);
    __publicField(this, "_components", {
      middle: {
        name: "DepositApplyBlock"
      },
      bottom: {
        name: "DepositOnlineScanNoteBlock"
      }
    });
    __publicField(this, "_specialActionComponents", {});
  }
};
var DepositPay988 = class extends DepositMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", DepositMethodType.PAY988);
    __publicField(this, "bindCardRequired", false);
    __publicField(this, "hasDownloadLink", true);
    __publicField(this, "openAgentLayer", true);
    __publicField(this, "_components", {
      middle: {
        name: "DepositApplyBlock"
      },
      bottom: {
        name: "Deposit988payNoteBlock"
      }
    });
    __publicField(this, "_specialActionComponents", {});
  }
};
var DepositPay808 = class extends DepositMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", DepositMethodType.PAY808);
    __publicField(this, "bindCardRequired", false);
    __publicField(this, "hasDownloadLink", true);
    __publicField(this, "openAgentLayer", true);
    __publicField(this, "_components", {
      middle: {
        name: "DepositApplyBlock"
      },
      bottom: {
        name: "Deposit808payNoteBlock"
      }
    });
    __publicField(this, "_specialActionComponents", {});
  }
};
var DepositKDPay = class extends DepositMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", DepositMethodType.KDPAY);
    __publicField(this, "bindCardRequired", false);
    __publicField(this, "hasDownloadLink", true);
    __publicField(this, "openAgentLayer", true);
    __publicField(this, "_components", {
      middle: {
        name: "DepositApplyBlock"
      },
      bottom: {
        name: "DepositKdpayNoteBlock"
      }
    });
    __publicField(this, "_specialActionComponents", {});
  }
};
var DepositUnionPayCloud = class extends DepositMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", DepositMethodType.UNION_PAY_CLOUD);
    __publicField(this, "bindCardRequired", true);
    __publicField(this, "hasDownloadLink", false);
    __publicField(this, "openAgentLayer", true);
    __publicField(this, "_components", {
      middle: {
        name: "DepositApplyBlock"
      },
      bottom: {
        name: "DepositUnionPayCommonNoteBlock"
      }
    });
    __publicField(this, "_specialActionComponents", {});
  }
};

// src/cashflow/depositMethods/DepositMethodFactory.ts
var deposits = {
  [DepositMethodType.ONLINE]: (openWindowAction, vt) => new DepositOnline(openWindowAction, vt),
  [DepositMethodType.ONLINE_MANUAL]: (openWindowAction, vt) => new DepositOnlineManual(openWindowAction, vt),
  [DepositMethodType.SCAN_WECHAT]: (openWindowAction, vt) => new DepositScanWechat(openWindowAction, vt),
  [DepositMethodType.SCAN_ALIPAY]: (openWindowAction, vt) => new DepositScanAlipay(openWindowAction, vt),
  [DepositMethodType.CRYPTO_ERC]: (openWindowAction, vt) => new DepositCryptoErc(openWindowAction, vt),
  [DepositMethodType.CRYPTO_TRC]: (openWindowAction, vt) => new DepositCryptoTrc(openWindowAction, vt),
  [DepositMethodType.GOUBAO]: (openWindowAction, vt) => new DepositGoubao(openWindowAction, vt),
  [DepositMethodType.TOPAY]: (openWindowAction, vt) => new DepositTopay(openWindowAction, vt),
  [DepositMethodType.EBPAY]: (openWindowAction, vt) => new DepositEbpay(openWindowAction, vt),
  [DepositMethodType.UNION_PAY_QUICK]: (openWindowAction, vt) => new DepositUnionPayQuick(openWindowAction, vt),
  [DepositMethodType.UNION_PAY_CLOUD]: (openWindowAction, vt) => new DepositUnionPayCloud(openWindowAction, vt),
  [DepositMethodType.CGPAY]: (openWindowAction, vt) => new DepositCGPay(openWindowAction, vt),
  [DepositMethodType.KOIPAY]: (openWindowAction, vt) => new DepositKOIPay(openWindowAction, vt),
  [DepositMethodType.E_CNY]: (openWindowAction, vt) => new DepositECNY(openWindowAction, vt),
  [DepositMethodType.PAY988]: (openWindowAction, vt) => new DepositPay988(openWindowAction, vt),
  [DepositMethodType.PAY808]: (openWindowAction, vt) => new DepositPay808(openWindowAction, vt),
  [DepositMethodType.KDPAY]: (openWindowAction, vt) => new DepositKDPay(openWindowAction, vt)
};
var DepositMethodFactory = {
  create(depositId, openWindowAction, vt) {
    const instance = deposits[depositId];
    if (instance === void 0) throw new Error("No DepositMethod instance");
    return instance(openWindowAction, vt);
  },
  has(depositId) {
    return deposits[depositId] !== void 0;
  }
};

// src/cashflow/withdrawalMethods/WithdrawalMethodBase.ts
var WithdrawalMethodBase = class {
  getComponents() {
    return this._components;
  }
};
var WithdrawalAgentTransfer = class extends WithdrawalMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", WithdrawWayType.AGENT_TRANSFER_WITHDRAW);
    __publicField(this, "bindCardRequired", false);
    __publicField(this, "hasDownloadLink", false);
    __publicField(this, "walletCategory", "debitCard");
    __publicField(this, "walletLength", null);
    __publicField(this, "currency", CurrencyType.RMB);
    __publicField(this, "_components", {
      middle: {
        name: "WithdrawalAgentTransferApplyBlock"
      },
      bottom: {
        name: "WithdrawalNoteBlock"
      }
    });
  }
};
var WithdrawalAliPay = class extends WithdrawalMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", WithdrawWayType.CURRENCY_ALIPAY);
    __publicField(this, "bindCardRequired", false);
    __publicField(this, "hasDownloadLink", false);
    __publicField(this, "walletCategory", "thirdParty");
    __publicField(this, "walletLength", 1);
    __publicField(this, "currency", CurrencyType.RMB);
    __publicField(this, "_components", {
      middle: {
        name: "WithdrawalAlipayApplyBlock"
      },
      bottom: {
        name: "WithdrawalNoteBlock"
      }
    });
  }
};
var WithdrawalCGPay = class extends WithdrawalMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", WithdrawWayType.CURRENCY_CGPAY);
    __publicField(this, "bindCardRequired", false);
    __publicField(this, "hasDownloadLink", true);
    __publicField(this, "walletLength", 1);
    __publicField(this, "walletCategory", "thirdParty");
    __publicField(this, "currency", CurrencyType.RMB);
    __publicField(this, "_components", {
      middle: {
        name: "WithdrawalCGPayApplyBlock"
      },
      bottom: {
        name: "WithdrawalCGPayNoteBlock"
      }
    });
  }
};
var WithdrawalDebitCard = class extends WithdrawalMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", WithdrawWayType.CURRENCY_DEBIT_CARD);
    __publicField(this, "bindCardRequired", false);
    __publicField(this, "hasDownloadLink", false);
    __publicField(this, "hasExchangeRate", false);
    __publicField(this, "walletCategory", "debitCard");
    __publicField(this, "walletLength", 5);
    __publicField(this, "currency", CurrencyType.RMB);
    __publicField(this, "_components", {
      middle: {
        name: "WithdrawalApplyBlock"
      },
      bottom: {
        name: "WithdrawalNoteBlock"
      }
    });
  }
};
var WithdrawalEbpay = class extends WithdrawalMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", WithdrawWayType.CURRENCY_EBPAY);
    __publicField(this, "bindCardRequired", false);
    __publicField(this, "hasDownloadLink", true);
    __publicField(this, "walletCategory", "crypto");
    __publicField(this, "walletLength", 1);
    __publicField(this, "currency", CurrencyType.RMB);
    __publicField(this, "_components", {
      middle: {
        name: "WithdrawalCryptoApplyBlock"
      },
      bottom: {
        name: "WithdrawalEbpayNoteBlock"
      }
    });
  }
};
var WithdrawalErc20 = class extends WithdrawalMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", WithdrawWayType.CURRENCY_CRYPTO_ERC);
    __publicField(this, "bindCardRequired", false);
    __publicField(this, "hasDownloadLink", false);
    __publicField(this, "walletCategory", "crypto");
    __publicField(this, "walletLength", 5);
    __publicField(this, "currency", CurrencyType.USDT);
    __publicField(this, "_components", {
      middle: {
        name: "WithdrawalCryptoApplyBlock"
      },
      bottom: {
        name: "WithdrawalCryptoNoteBlock"
      }
    });
  }
};
var WithdrawalGoubao = class extends WithdrawalMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", WithdrawWayType.CURRENCY_GOUBAO);
    __publicField(this, "bindCardRequired", false);
    __publicField(this, "hasDownloadLink", true);
    __publicField(this, "walletCategory", "thirdParty");
    __publicField(this, "walletLength", 1);
    __publicField(this, "currency", CurrencyType.RMB);
    __publicField(this, "_components", {
      middle: {
        name: "WithdrawalGoubaoApplyBlock"
      },
      bottom: {
        name: "WithdrawalGoubaoNoteBlock"
      }
    });
  }
};
var WithdrawalKDPay = class extends WithdrawalMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", WithdrawWayType.CURRENCY_KDPAY);
    __publicField(this, "bindCardRequired", false);
    __publicField(this, "hasDownloadLink", true);
    __publicField(this, "walletCategory", "crypto");
    __publicField(this, "walletLength", 1);
    __publicField(this, "currency", CurrencyType.RMB);
    __publicField(this, "_components", {
      middle: {
        name: "WithdrawalCryptoApplyBlock"
      },
      bottom: {
        name: "WithdrawalKdpayNoteBlock"
      }
    });
  }
};
var WithdrawalKOIPay = class extends WithdrawalMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", WithdrawWayType.CURRENCY_KOIPAY);
    __publicField(this, "bindCardRequired", false);
    __publicField(this, "hasDownloadLink", true);
    __publicField(this, "walletCategory", "crypto");
    __publicField(this, "walletLength", 1);
    __publicField(this, "currency", CurrencyType.RMB);
    __publicField(this, "_components", {
      middle: {
        name: "WithdrawalCryptoApplyBlock"
      },
      bottom: {
        name: "WithdrawalKoipayNoteBlock"
      }
    });
  }
};
var WithdrawalPay808 = class extends WithdrawalMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", WithdrawWayType.CURRENCY_PAY808);
    __publicField(this, "bindCardRequired", false);
    __publicField(this, "hasDownloadLink", true);
    __publicField(this, "walletCategory", "crypto");
    __publicField(this, "walletLength", 1);
    __publicField(this, "currency", CurrencyType.RMB);
    __publicField(this, "_components", {
      middle: {
        name: "WithdrawalCryptoApplyBlock"
      },
      bottom: {
        name: "Withdrawal808payNoteBlock"
      }
    });
  }
};
var WithdrawalPay988 = class extends WithdrawalMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", WithdrawWayType.CURRENCY_PAY988);
    __publicField(this, "bindCardRequired", false);
    __publicField(this, "hasDownloadLink", true);
    __publicField(this, "walletCategory", "crypto");
    __publicField(this, "walletLength", 1);
    __publicField(this, "currency", CurrencyType.RMB);
    __publicField(this, "_components", {
      middle: {
        name: "WithdrawalCryptoApplyBlock"
      },
      bottom: {
        name: "Withdrawal988payNoteBlock"
      }
    });
  }
};
var WithdrawalTopay = class extends WithdrawalMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", WithdrawWayType.CURRENCY_TOPAY);
    __publicField(this, "bindCardRequired", false);
    __publicField(this, "hasDownloadLink", true);
    __publicField(this, "hasExchangeRate", true);
    __publicField(this, "walletCategory", "crypto");
    __publicField(this, "walletLength", 1);
    __publicField(this, "currency", CurrencyType.RMB);
    __publicField(this, "_components", {
      middle: {
        name: "WithdrawalCryptoApplyBlock"
      },
      bottom: {
        name: "WithdrawalTopayNoteBlock"
      }
    });
  }
};
var WithdrawalTrc20 = class extends WithdrawalMethodBase {
  constructor() {
    super(...arguments);
    __publicField(this, "id", WithdrawWayType.CURRENCY_CRYPTO_ERC);
    __publicField(this, "bindCardRequired", false);
    __publicField(this, "hasDownloadLink", false);
    __publicField(this, "hasExchangeRate", true);
    __publicField(this, "walletCategory", "crypto");
    __publicField(this, "walletLength", 5);
    __publicField(this, "currency", CurrencyType.USDT);
    __publicField(this, "_components", {
      middle: {
        name: "WithdrawalCryptoApplyBlock"
      },
      bottom: {
        name: "WithdrawalCryptoNoteBlock"
      }
    });
  }
};
var withdrawals = {
  [WithdrawWayType.AGENT_TRANSFER_WITHDRAW]: () => new WithdrawalAgentTransfer(),
  [WithdrawWayType.CURRENCY_CGPAY]: () => new WithdrawalCGPay(),
  [WithdrawWayType.CURRENCY_CGPAY_USDT]: () => new WithdrawalCGPay(),
  [WithdrawWayType.CURRENCY_EBPAY]: () => new WithdrawalEbpay(),
  [WithdrawWayType.CURRENCY_TOPAY]: () => new WithdrawalTopay(),
  [WithdrawWayType.CURRENCY_KOIPAY]: () => new WithdrawalKOIPay(),
  [WithdrawWayType.CURRENCY_DEBIT_CARD]: () => new WithdrawalDebitCard(),
  [WithdrawWayType.CURRENCY_CRYPTO_ERC]: () => new WithdrawalErc20(),
  [WithdrawWayType.CURRENCY_CRYPTO_TRC]: () => new WithdrawalTrc20(),
  [WithdrawWayType.CURRENCY_GOUBAO]: () => new WithdrawalGoubao(),
  [WithdrawWayType.CURRENCY_PAY988]: () => new WithdrawalPay988(),
  [WithdrawWayType.CURRENCY_PAY808]: () => new WithdrawalPay808(),
  [WithdrawWayType.CURRENCY_KDPAY]: () => new WithdrawalKDPay(),
  [WithdrawWayType.CURRENCY_ALIPAY]: () => new WithdrawalAliPay()
};
var WithdrawalMethodFactory = {
  create(withdrawalId) {
    const instance = withdrawals[withdrawalId];
    if (instance === void 0) throw new Error("No WithdrawalMethod instance");
    return instance();
  },
  has(withdrawalId) {
    return withdrawals[withdrawalId] !== void 0;
  }
};

// src/cashflow/depositMethods/action/ActionJS.ts
var ActionJS = class {
  constructor(data, argument) {
    this.data = data;
    this.argument = argument;
    __publicField(this, "finishedCallback", () => {
      throw new Error("no set closed callback");
    });
  }
};

export { ActionJS, ActionKind, DepositCGPay, DepositCryptoErc, DepositCryptoTrc, DepositECNY, DepositEbpay, DepositGoubao, DepositKDPay, DepositKOIPay, DepositMethodBase, DepositMethodFactory, DepositOnline, DepositOnlineManual, DepositPay808, DepositPay988, DepositScanAlipay, DepositScanWechat, DepositTopay, DepositUnionPayCloud, DepositUnionPayQuick, LOADING_HTML, ONE_DAY_IN_MS, PERMANENT_TIME, THREE_DAYS_IN_MS, THREE_HOURS_IN_MS, TRANSPARENT_IMAGE, WithdrawalAgentTransfer, WithdrawalAliPay, WithdrawalCGPay, WithdrawalDebitCard, WithdrawalEbpay, WithdrawalErc20, WithdrawalGoubao, WithdrawalKDPay, WithdrawalKOIPay, WithdrawalMethodBase, WithdrawalMethodFactory, WithdrawalPay808, WithdrawalPay988, WithdrawalTopay, WithdrawalTrc20, announcementIcon, canvasToImage, copyString, createShouldDisableDate, createShouldDisableDateBetween, createShouldDisableFuture, createTokenRefreshHandler, endOfDayTaipei, endOfMonthTaipei, endOfWeekTaipei, endOfYesterdayTaipei, filterHTMLTags, formatTaipeiTime, getAllowDateRangeOfOneMonths, getAllowDateRangeOfTwoWeeks, getAllowOffsetDaysRange, getAllowedTimeStamps, getBankIcon, getBankImage, getCorrectedDateRange, getDateAtQuery, getDateQuery, getDayArray, getDefaultDateRangeOfToday, getIdleStorage, getLastWeek, getMaxInteger, getMonthArray, getPlayerRankImage, getRangeOfYesterday, getTaipeiAllowDateRangeOfTwoWeeks, getTaipeiAllowOffsetDaysRange, getTaipeiDefaultDateRangeOfToday, getTaipeiLastWeek, getTaipeiNowTime, getTaipeiRangeOfYesterday, getTaipeiThisWeek, getTaipeiTime, getThisMonth, getThisWeek, getWebpClass, getWeekArray, goldenCashFormat, htmlToCanvas, isEditableSystemActivitiesHelper, loadScript, normalizeAlphanumeric, normalizeMaxNumber, normalizeMinNumber, normalizeNestedJson, openQQ, openWindow, recursivelyParseJSON, registerIdleLogout, scrollIntoView, scrollMore, setLastMonth, setLastSevenDays, setLastTwoMonths, setLastWeek, setPermanent, setThisMonth, setThisMonthToYesterday, setThisWeek, setToday, setTomorrow, setUpcomingDays, setUpcomingDaysFromNow, setUpcomingHoursFromNow, setViewportOnDesktop, setWindowLoading, setYesterday, startOfDayTaipei, startOfMonthTaipei, startOfWeekTaipei, startOfYearTaipei, startOfYesterdayTaipei, supportWebp, toTimezonedDate };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map